import { map, some, sum } from 'lodash';

import { getDefaultModifiedModelAdd } from 'app/main/configs';

import type ModuleButtonsType from '../components/ModuleButtonsType';
import { showDetailsReportingDialog, Utils } from 'app/main/components';
import { showDeleteDialog, showDocumentSettingsEditDialog } from 'app/main/components/dialogs';
import { showAddNotesDialog } from 'app/main/submodules/notes/NotesAddDialog';
import { showInvoicesModuleEditDialog } from 'app/main/submodules/invoices/components/InvoicesModuleEditDialog';
import { shareDetailsPage } from 'app/main/components/dialogs/ShareDialog';
import { showFastAddDialog } from '../components/FastAddDialog';
import { showOrdersLinesEditDialog } from './lines/OrdersLinesEditDialog';
import { showOrdersBillingEditDialog } from './billing/OrdersBillingEditDialog';

const isBeb = Utils.getConnectionFromToken() === 'PERCEPTIVE_CONTROL_BEB';

function isOrderNotEditable(authorizer, model) {
	return authorizer.cannot('update', 'orders') || ![1, 4, 5].includes(model.ords_orst_id);
}

function createCopy(params) {
	Utils.executeStored({
		action: 'st_companies_get_details',
		dispatch: params.dispatch,
		params: { comp_uid: params.model.ords_comp_uid },
		successHandler: ({ data }) => {
			params.dispatch(
				showFastAddDialog({
					module: 'orders',
					updateRefresh: params.updateRefresh,
					model: {
						...params.model,
						...getDefaultModifiedModelAdd(params),
						ords_parent_ords_uid: params.model.ords_uid,
						ords_user_assigned_uid: data.comp_user_assigned_uid || null,
						ords_user_assigned_2_uid: data.comp_user_assigned_2_uid || null,
						ords_d_delivery_requested: null,
						ords_pric_id: data.comp_pric_id || null,
						ords_disc_id: data.comp_disc_id || null,
						ords_pate_id: data.comp_pate_id || null,
						ords_pame_id: data.comp_pame_id || null,
						ords_shme_id: data.comp_shme_id || null,
						ords_shag_id: data.comp_shag_id || null,
						ords_revisionnumber: null,
						_isCopy: true
					}
				})
			);
		}
	});
}

function createRevision(params) {
	params.dispatch(
		showFastAddDialog({
			module: 'orders',
			updateRefresh: params.updateRefresh,
			model: {
				...params.model,
				ords_orst_id: 1,
				ords_d_sign: Utils.today(),
				ords_parent_ords_uid: params.model.ords_uid,
				ords_revisionnumber: (params.model.ords_revisionnumber || 0) + 1,
				_isCopy: true
			},
			onAfterSaveHandler: () => {
				Utils.executeStored({
					action: 'st_orders_update',
					dispatch: params.dispatch,
					data: {
						ords_uid: params.model.ords_uid,
						ords_user_modified_uid: params.userData.user_uid,
						ords_orst_id: 7
					},
					successHandler: () => params.updateRefresh()
				});
			}
		})
	);
}

function openReportPdf({ dispatch, ordsUid, templateName, fileName }) {
	Utils.executeDownloadFile({
		url: '/api/notification/liquid/report/pdf',
		body: {
			templateName,
			queryParams: { ords_uid: ordsUid }
		},
		fileName: `${fileName}.pdf`,
		dispatch
		// successHandler: response => {
		// 	const blob = new Blob([response.data], { type: response.headers['content-type'] });
		// 	Utils.openLink({ pathname: URL.createObjectURL(blob), newTab: true, external: true });
		// }
	});
}

const buttons: ModuleButtonsType = {
	view: {
		itemButtons: [
			// {
			// 	name: 'VIEW_ITEM_BUTTON_FAVOURITE',
			// 	icon: 'star-outline',
			// 	disabled: () => true,
			// 	action: () => {}
			// },
			{
				name: 'VIEW_ITEM_BUTTON_EDIT',
				icon: 'edit',
				hidden: ({ authorizer, model }) => isOrderNotEditable(authorizer, model),
				action: ({ dispatch, updateRefresh, model }) =>
					dispatch(showFastAddDialog({ module: 'orders', updateRefresh, isUpdate: true, model }))
			},
			{
				name: 'VIEW_ITEM_BUTTON_ADD_COPY',
				icon: 'content-copy',
				iconAdd: true,
				hidden: ({ authorizer }) => authorizer.cannot('create', 'orders'),
				action: createCopy
			},
			{
				name: 'DETAILS_HEADER_BUTTON_ORDERS_CREATE_REVISION',
				icon: 'content-copy',
				iconAdd: true,
				hidden: ({ authorizer, model }) => isOrderNotEditable(authorizer, model),
				action: createRevision
			},
			// {
			// 	name: 'VIEW_ITEM_BUTTON_NEW_NOTE',
			// 	icon: 'comment-text-multiple-outline',
			// 	iconAdd: true,
			// 	action: ({ model, dispatch }) =>
			// 		dispatch(showAddNotesDialog({ model: { note_table: 'orders', note_table_uid: model.ords_uid } }))
			// },
			{
				name: 'VIEW_ITEM_BUTTON_NEW_PROJECT',
				icon: 'clipboard-text-outline',
				iconAdd: true,
				hidden: ({ isModuleDisabled, authorizer }) =>
					isModuleDisabled('projects') || authorizer.cannot('create', 'projects'),
				action: ({ dispatch, updateRefresh, model }) =>
					dispatch(
						showFastAddDialog({
							module: 'projects',
							updateRefresh,
							model: {
								proj_maac_uid: model.ords_maac_uid,
								proj_dems_uid: model.ords_dems_uid,
								proj_oppo_uid: model.ords_oppo_uid,
								proj_cntr_uid: model.ords_cntr_uid,
								proj_ords_uid: model.ords_uid,
								proj_parent_proj_uid: model.ords_proj_uid,
								proj_acti_uid: model.ords_acti_uid,
								proj_wore_uid: model.ords_wore_uid,
								proj_chro_uid: model.ords_chro_uid,
								proj_tick_uid: model.ords_tick_uid,
								proj_comp_uid: model.ords_comp_uid,
								proj_peop_uid: model.ords_peop_uid
							}
						})
					)
			},
			{
				name: 'VIEW_ITEM_BUTTON_NEW_ACTIVITY',
				icon: 'puzzle-outline',
				iconAdd: true,
				hidden: ({ isModuleDisabled, authorizer }) =>
					isModuleDisabled('activities') || authorizer.cannot('create', 'activities'),
				action: ({ dispatch, updateRefresh, model }) =>
					dispatch(
						showFastAddDialog({
							module: 'activities',
							updateRefresh,
							model: {
								acti_maac_uid: model.ords_maac_uid,
								acti_dems_uid: model.ords_dems_uid,
								acti_oppo_uid: model.ords_oppo_uid,
								acti_cntr_uid: model.ords_cntr_uid,
								acti_ords_uid: model.ords_uid,
								acti_proj_uid: model.ords_proj_uid,
								acti_parent_acti_uid: model.ords_acti_uid,
								acti_wore_uid: model.ords_wore_uid,
								acti_chro_uid: model.ords_chro_uid,
								acti_tick_uid: model.ords_tick_uid,
								acti_comp_uid: model.ords_comp_uid,
								acti_peop_uid: model.ords_peop_uid
							}
						})
					)
			},
			{
				name: 'VIEW_ITEM_BUTTON_NEW_WORKREPORT',
				icon: 'text-box-multiple-outline',
				iconAdd: true,
				hidden: ({ isModuleDisabled, authorizer }) =>
					isModuleDisabled('workreports') || authorizer.cannot('create', 'workreports'),
				action: ({ dispatch, updateRefresh, model }) =>
					dispatch(
						showFastAddDialog({
							module: 'workreports',
							updateRefresh,
							model: {
								wore_maac_uid: model.ords_maac_uid,
								wore_dems_uid: model.ords_dems_uid,
								wore_oppo_uid: model.ords_oppo_uid,
								wore_cntr_uid: model.ords_cntr_uid,
								wore_ords_uid: model.ords_uid,
								wore_proj_uid: model.ords_proj_uid,
								wore_acti_uid: model.ords_acti_uid,
								wore_parent_wore_uid: model.ords_wore_uid,
								wore_chro_uid: model.ords_chro_uid,
								wore_tick_uid: model.ords_tick_uid,
								wore_comp_uid: model.ords_comp_uid,
								wore_peop_uid: model.ords_peop_uid
							}
						})
					)
			},
			{
				name: 'VIEW_ITEM_BUTTON_DELETE',
				icon: 'delete',
				color: 'red',
				hidden: ({ authorizer }) => authorizer.cannot('delete', 'orders'),
				action: ({ model, dispatch, updateRefresh }) =>
					dispatch(
						showDeleteDialog({
							module: 'orders',
							id: model.ords_uid,
							title: model.ords_title,
							updateRefresh
						})
					)
			}
		],
		headerButtons: [
			// {
			// 	name: 'VIEW_HEADER_BUTTON_SELECT',
			// 	icon: 'square-outline',
			// 	action: ({ switchIsSelecting }) => switchIsSelecting()
			// },
			{
				name: 'VIEW_HEADER_BUTTON_NEW',
				icon: 'plus-circle',
				hidden: ({ authorizer }) => authorizer.cannot('create', 'orders') || isBeb,
				action: () => Utils.openLink({ pathname: '/orders/add' })
			}
		]
	},
	details: {
		headerButtons: [
			// {
			// 	name: 'DETAILS_HEADER_BUTTON_FAVOURITE',
			// 	icon: 'star-outline',
			// 	disabled: () => true,
			// 	action: () => {}
			// },
			// {
			// 	name: 'DETAILS_HEADER_BUTTON_EXPORT',
			// 	icon: 'file-export',
			// 	action: ({ model, dispatch, settings }) => showDetailsReportingDialog({ model, dispatch, settings })
			// },
			{
				name: 'DETAILS_HEADER_BUTTON_ORDERS_CREATE_REVISION',
				icon: 'content-copy',
				iconAdd: true,
				hidden: ({ authorizer }) => authorizer.cannot('create', 'orders'),
				action: createRevision
			},
			{
				name: 'DETAILS_HEADER_BUTTON_ORDERS_PUSH_TO_NAV',
				icon: 'upload',
				hidden: ({ authorizer, model }) =>
					authorizer.cannot('update', 'orders') || ![1, 4, 5].includes(model.ords_orst_id),
				disabled: ({ model }) => !!model.ords_error || model.comp_core_id !== 2,
				action: ({ dispatch, model, sysSettings, t, updateRefresh }) => {
					if (model.ords_tot_merchpoints_spent > model.ords_tot_merchpoints_earned) {
						dispatch(Utils.alertMsg(t('ORDERS_MERCHPOINTS_ERROR'), 'error'));
					} else if (model.ords_tot_value_tax_excl < sysSettings.b2b_min_order_value) {
						dispatch(Utils.alertMsg(t('ORDERS_MIN_VALUE_ERROR'), 'error'));
					} else {
						dispatch(Utils.alertProgressMsg(t));
						Utils.executeStored({
							action: 'st_nav_push_orders',
							dispatch,
							params: { ords_uid: model.ords_uid },
							successHandler: () => {
								dispatch(Utils.alertSuccessMsg(t));
								updateRefresh();
							}
						});
					}
				}
			},
			{
				name: 'DETAILS_HEADER_BUTTON_ORDERS_EXPORT_PDF_IT',
				icon: 'file-download',
				action: ({ dispatch, model }) => {
					openReportPdf({
						dispatch,
						ordsUid: model.ords_uid,
						templateName: isBeb ? 'order-proposal-it-beb' : 'order-proposal-it-pf',
						fileName: model.ords_title
					});
				}
			},
			{
				name: 'DETAILS_HEADER_BUTTON_ORDERS_EXPORT_PDF_EN',
				icon: 'file-download',
				action: ({ dispatch, model }) => {
					openReportPdf({
						dispatch,
						ordsUid: model.ords_uid,
						templateName: isBeb ? 'order-proposal-en-beb' : 'order-proposal-en-pf',
						fileName: model.ords_title
					});
				}
			},
			{
				name: 'DETAILS_HEADER_BUTTON_ORDERS_EXPORT_XLSX',
				icon: 'file-download',
				action: ({ dispatch, model }) => {
					Utils.executeDownloadFile({
						url: `/api/reporting/reports/generate/reports/crm/${
							isBeb ? 'BEB' : 'PF'
						}/orders_lines/list/ORDERS_LINES_LIST`,
						body: {
							ReportFormat: 'xlsx',
							ReportFileName: `${model.ords_title}.xlsx`,
							Parameters: {
								FILTERS_JSON: [
									{ filt_field: 'orli_ords_uid', filt_clause: 'equals', filt_value: model.ords_uid }
								]
							}
						},
						fileName: model.ords_title,
						dispatch
					});
				}
			},
			{
				name: 'DETAILS_HEADER_BUTTON_DOCUMENTS_SETTINGS',
				icon: 'folder-wrench',
				disabled: ({ model, sysSettings }) => !sysSettings[`docs_${model.core_type}_path`],
				hidden: ({ authorizer, sysSettings }) =>
					sysSettings.docs_enabled !== 'true' ||
					sysSettings.docs_orders_enabled !== 'true' ||
					authorizer.cannot('update', 'orders'),
				action: ({ model, dispatch, sysSettings, updateRefresh }) =>
					dispatch(
						showDocumentSettingsEditDialog({
							module: 'orders',
							docsFolderPath: `${sysSettings[`docs_${model.core_type}_path`]}\\${model.comp_foldername}\\Ordini\\`,
							itemUid: model.ords_uid,
							folderName: model.ords_foldername,
							updateRefresh
						})
					)
			},
			{
				name: 'DETAILS_HEADER_BUTTON_SHARE',
				icon: 'share-variant',
				action: ({ model, dispatch, settings, t }) => shareDetailsPage({ model, dispatch, settings, t })
			},
			{
				name: 'DETAILS_HEADER_BUTTON_SHOW_LOG',
				icon: 'timeline-clock-outline',
				disabled: ({ isEdit }) => isEdit,
				hidden: ({ authorizer }) => authorizer.cannot('view', 'logs'),
				action: ({ switchShowLogs }) => switchShowLogs()
			},
			{
				name: 'DETAILS_HEADER_BUTTON_DELETE',
				icon: 'delete',
				color: 'red',
				hidden: ({ authorizer }) => authorizer.cannot('delete', 'orders'),
				action: ({ model, dispatch }) =>
					dispatch(
						showDeleteDialog({
							module: 'orders',
							id: model.ords_uid,
							title: model.ords_title
						})
					)
			}
		],
		fabs: [
			{
				id: 'summary',
				name: 'ACTION_EDIT',
				icon: 'edit',
				hidden: ({ authorizer, model, isEdit }) => (!isEdit ? isOrderNotEditable(authorizer, model) : false),
				action: ({ switchEdit }) => switchEdit()
			},
			{
				id: 'lines',
				name: 'ORDERS_ACTION_EDIT_LINES',
				icon: 'edit',
				hidden: ({ authorizer, model }) => isOrderNotEditable(authorizer, model),
				options: [
					{
						name: 'DETAILS_ORDERS_LINES_BUTTON_ADD_ITEM',
						icon: 'package-variant-closed-plus',
						hidden: ({ additionalProps }) => additionalProps.selectedOrderLines?.length > 0 || isBeb,
						action: ({ dispatch, model, updateRefresh }) =>
							dispatch(
								showOrdersLinesEditDialog({
									baseModel: { orli_ords_uid: model.ords_uid, orli_type: 'item' },
									discountPercentage: model.ords_pric_id,
									pricingGroupId: model.disc_percentage,
									compUid: model.ords_comp_uid,
									isAdd: true,
									updateRefresh
								})
							)
					},
					{
						name: 'DETAILS_ORDERS_LINES_BUTTON_ADD_PRICING_ITEMS',
						icon: 'package-variant-closed-plus',
						disabled: ({ additionalProps }) => additionalProps.selectedOrderLines?.length > 0,
						action: ({ additionalProps }) => additionalProps.openLinesAddDialog()
					},
					{
						name: 'DETAILS_ORDERS_LINES_BUTTON_ADD_SERVICE',
						icon: 'briefcase-plus',
						hidden: ({ sysSettings, additionalProps }) =>
							sysSettings.orders_lines_workreports_enabled !== 'true' ||
							additionalProps.selectedOrderLines?.length > 0,
						action: ({ dispatch, model, updateRefresh }) =>
							dispatch(
								showOrdersLinesEditDialog({
									baseModel: { orli_ords_uid: model.ords_uid, orli_type: 'workreports' },
									discountPercentage: model.ords_pric_id,
									pricingGroupId: model.disc_percentage,
									compUid: model.ords_comp_uid,
									isAdd: true,
									updateRefresh
								})
							)
					},
					{
						name: 'DETAILS_ORDERS_LINES_BUTTON_ADD_NOTE',
						icon: 'text-box-plus',
						hidden: ({ additionalProps }) => additionalProps.selectedOrderLines?.length > 0 || isBeb,
						action: ({ additionalProps }) => additionalProps.openNoteAddDialog()
					},
					{
						name: 'DETAILS_ORDERS_LINES_BUTTON_ADD_STANDARD_NOTES',
						icon: 'text-box-plus',
						disabled: ({ additionalProps }) => additionalProps.selectedOrderLines?.length > 0,
						hidden: () => isBeb,
						action: ({ additionalProps }) => additionalProps.openStandardNotesAddDialog()
					},
					{
						name: 'DETAILS_ORDERS_LINES_BUTTON_ADD_PROJECT',
						icon: 'clipboard-text-outline',
						disabled: ({ additionalProps }) => some(additionalProps.selectedOrderLines, 'orli_proj_uid'),
						hidden: ({ sysSettings, additionalProps }) =>
							sysSettings.orders_lines_projects_enabled !== 'true' ||
							(additionalProps.selectedOrderLines || []).length === 0,
						action: ({ dispatch, model, updateRefresh, additionalProps }) => {
							const orderLines = additionalProps.selectedOrderLines || [];
							const workReportsLines = orderLines.filter(line => line.orli_type === 'workreports');
							additionalProps.setSelectedLines([]);
							dispatch(
								showFastAddDialog({
									module: 'projects',
									updateRefresh,
									model: {
										proj_title: model.ords_title,
										proj_maac_uid: model.ords_maac_uid,
										proj_dems_uid: model.ords_dems_uid,
										proj_oppo_uid: model.ords_oppo_uid,
										proj_cntr_uid: model.ords_cntr_uid,
										proj_ords_uid: model.ords_uid,
										proj_parent_proj_uid: model.ords_proj_uid,
										proj_acti_uid: model.ords_acti_uid,
										proj_wore_uid: model.ords_wore_uid,
										proj_chro_uid: model.ords_chro_uid,
										proj_tick_uid: model.ords_tick_uid,
										proj_comp_uid: model.ords_comp_uid,
										proj_peop_uid: model.ords_peop_uid,
										proj_parent_orli_uid: map(orderLines, 'orli_uid'),
										proj_value_planned: sum(map(orderLines, 'orli_tot_value_tax_excl')),
										proj_expenses_planned: sum(map(orderLines, 'orli_tot_expenses')),
										proj_length_hours_planned: sum(map(workReportsLines, 'orli_quantity'))
									}
								})
							);
						}
					},
					{
						name: 'DETAILS_ORDERS_LINES_BUTTON_DELETE_LINE',
						icon: 'bin',
						disabled: ({ additionalProps }) => (additionalProps.selectedOrderLines || []).length === 0,
						action: ({ additionalProps }) =>
							additionalProps.showDeleteLinesDialog(additionalProps.selectedOrderLines)
					}
				]
			},
			{
				id: 'billing',
				name: 'ORDERS_ACTION_EDIT_BILLING',
				icon: 'edit',
				hidden: ({ authorizer, model }) => authorizer.cannot('update', 'orders') || model.ords_orst_id === 2,
				options: [
					{
						name: 'DETAILS_ORDERS_BILLING_BUTTON_ADD_LINE',
						icon: 'package-variant-closed-plus',
						hidden: ({ additionalProps }) => additionalProps.selectedOrderBillingLines?.length > 0,
						action: ({ dispatch, model, updateRefresh }) =>
							dispatch(
								showOrdersBillingEditDialog({
									baseModel: { orbi_ords_uid: model.ords_uid },
									isAdd: true,
									updateRefresh
								})
							)
					},
					{
						name: 'DETAILS_ORDERS_BILLING_BUTTON_DELETE_LINE',
						icon: 'bin',
						hidden: ({ additionalProps }) => (additionalProps.selectedOrderBillingLines || []).length === 0,
						action: ({ additionalProps }) =>
							additionalProps.showDeleteBillingDialog(additionalProps.selectedOrderBillingLines)
					}
				]
			},
			{
				id: 'invoices',
				name: 'VIEW_ITEM_BUTTON_NEW_INVOICE',
				icon: 'invoice-text-outline',
				iconAdd: true,
				hidden: ({ isModuleDisabled, authorizer }) =>
					isModuleDisabled('invoices') || authorizer.cannot('create', 'invoices'),
				action: ({ dispatch, updateRefresh, model }) =>
					dispatch(
						showInvoicesModuleEditDialog({
							baseModel: {
								invc_comp_uid: model.ords_comp_uid,
								invc_pate_id: model.ords_pate_id,
								invc_pame_id: model.ords_pame_id
							},
							newRowBaseModel: {
								inrw_table: 'orders',
								inrw_table_uid: model.ords_uid,
								inrw_table_title: model.ords_title,
								comp_title: model.comp_title
							},
							updateRefresh,
							isAdd: true
						})
					)
			},
			{
				id: 'opportunities',
				name: 'VIEW_ITEM_BUTTON_NEW_OPPORTUNITY',
				icon: 'thought-bubble-outline',
				iconAdd: true,
				hidden: ({ isModuleDisabled, authorizer }) =>
					isModuleDisabled('opportunities') || authorizer.cannot('create', 'opportunities'),
				action: ({ dispatch, updateRefresh, model }) =>
					dispatch(
						showFastAddDialog({
							module: 'opportunities',
							updateRefresh,
							model: {
								oppo_maac_uid: model.ords_maac_uid,
								oppo_dems_uid: model.ords_dems_uid,
								oppo_parent_oppo_uid: model.ords_oppo_uid,
								oppo_quot_uid: model.ords_quot_uid,
								oppo_cntr_uid: model.ords_cntr_uid,
								oppo_ords_uid: model.ords_uid,
								oppo_proj_uid: model.ords_proj_uid,
								oppo_acti_uid: model.ords_acti_uid,
								oppo_wore_uid: model.ords_wore_uid,
								oppo_chro_uid: model.ords_chro_uid,
								oppo_tick_uid: model.ords_tick_uid,
								oppo_comp_uid: model.ords_comp_uid,
								oppo_peop_uid: model.ords_peop_uid
							}
						})
					)
			},
			{
				id: 'projects',
				name: 'VIEW_ITEM_BUTTON_NEW_PROJECT',
				icon: 'clipboard-text-outline',
				iconAdd: true,
				hidden: ({ isModuleDisabled, authorizer }) =>
					isModuleDisabled('projects') || authorizer.cannot('create', 'projects'),
				action: ({ dispatch, updateRefresh, model }) =>
					dispatch(
						showFastAddDialog({
							module: 'projects',
							updateRefresh,
							model: {
								proj_maac_uid: model.ords_maac_uid,
								proj_dems_uid: model.ords_dems_uid,
								proj_oppo_uid: model.ords_oppo_uid,
								proj_cntr_uid: model.ords_cntr_uid,
								proj_ords_uid: model.ords_uid,
								proj_parent_proj_uid: model.ords_proj_uid,
								proj_acti_uid: model.ords_acti_uid,
								proj_wore_uid: model.ords_wore_uid,
								proj_chro_uid: model.ords_chro_uid,
								proj_tick_uid: model.ords_tick_uid,
								proj_comp_uid: model.ords_comp_uid,
								proj_peop_uid: model.ords_peop_uid
							}
						})
					)
			},
			{
				id: 'activities',
				name: 'VIEW_ITEM_BUTTON_NEW_ACTIVITY',
				icon: 'puzzle-outline',
				iconAdd: true,
				hidden: ({ isModuleDisabled, authorizer }) =>
					isModuleDisabled('activities') || authorizer.cannot('create', 'activities'),
				action: ({ dispatch, updateRefresh, model }) =>
					dispatch(
						showFastAddDialog({
							module: 'activities',
							updateRefresh,
							model: {
								acti_maac_uid: model.ords_maac_uid,
								acti_dems_uid: model.ords_dems_uid,
								acti_oppo_uid: model.ords_oppo_uid,
								acti_cntr_uid: model.ords_cntr_uid,
								acti_ords_uid: model.ords_uid,
								acti_proj_uid: model.ords_proj_uid,
								acti_parent_acti_uid: model.ords_acti_uid,
								acti_wore_uid: model.ords_wore_uid,
								acti_chro_uid: model.ords_chro_uid,
								acti_tick_uid: model.ords_tick_uid,
								acti_comp_uid: model.ords_comp_uid,
								acti_peop_uid: model.ords_peop_uid
							}
						})
					)
			},
			{
				id: 'workreports',
				name: 'VIEW_ITEM_BUTTON_NEW_WORKREPORT',
				icon: 'text-box-multiple-outline',
				iconAdd: true,
				hidden: ({ isModuleDisabled, authorizer }) =>
					isModuleDisabled('workreports') || authorizer.cannot('create', 'workreports'),
				action: ({ dispatch, updateRefresh, model }) =>
					dispatch(
						showFastAddDialog({
							module: 'workreports',
							updateRefresh,
							model: {
								wore_maac_uid: model.ords_maac_uid,
								wore_dems_uid: model.ords_dems_uid,
								wore_oppo_uid: model.ords_oppo_uid,
								wore_cntr_uid: model.ords_cntr_uid,
								wore_ords_uid: model.ords_uid,
								wore_proj_uid: model.ords_proj_uid,
								wore_acti_uid: model.ords_acti_uid,
								wore_parent_wore_uid: model.ords_wore_uid,
								wore_chro_uid: model.ords_chro_uid,
								wore_tick_uid: model.ords_tick_uid,
								wore_comp_uid: model.ords_comp_uid,
								wore_peop_uid: model.ords_peop_uid
							}
						})
					)
			}
		]
	}
};

export default buttons;
